import React, { useRef, useState, useEffect } from 'react';
import styled from 'astroturf/react';

import { Icon } from 'js/design_system/Icon';
import $image_url from 'js/utils/image_url';
import trackClick from 'js/utils/trackClick';
import { currentUser } from 'js/utils/user';
import useOnClickOutside from 'js/utils/hooks/useOnClickOutside';
import AccountMenu from 'js/header/FocusedNav/AccountMenu';
import DoubleDigitCountBubble from 'js/header/FocusedNav/DoubleDigitCountBubble';
import { AccountMenuProps } from 'js/header/FocusedNav/AccountMenu';

const PositionedDoubleDigitCountBubble = styled(DoubleDigitCountBubble)`
  position: absolute;
  top: 2px;
  left: 30px;
`;

// `redesign` gates the design_2026_q2_nav_redesign visual change: the control
// arm keeps the pre-branch border-radius + hover/focus styling; the redesign
// arm uses the design-system secondary-button tokens. The `.redesign` rules
// out-specify the base (control) rules so they win when the class is applied.
const UserButton = styled.button<{ small?: boolean; redesign?: boolean }>`
  position: relative;
  height: 48px;
  padding: 4px 16px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  background: #e4ebf7;
  border: 1px solid #cedbf1;
  color: #0645b1;
  cursor: pointer;
  flex-grow: 0;
  max-width: 200px;

  display: flex;
  align-items: center;

  // control (pre-redesign) hover/focus states
  &:hover,
  &:active,
  &:visited {
    color: #0645b1;
  }
  &:hover,
  &:focus {
    background: #0057eb;
    color: white;
  }

  &.redesign {
    border-radius: var(--border-radius-radius-lg);
  }
  // Redesign hover + selected (menu open) states mirror the DS secondary button.
  &.redesign:hover {
    background: var(--button-secondary-fill-hover, #e6ecf7);
    color: var(--button-secondary-text-hover, #082f75);
    border-color: var(--button-secondary-stroke-hover, #386ac1);
  }
  &.redesign:active,
  &.redesign[aria-pressed='true'] {
    background: var(--button-secondary-fill-press, #cddaef);
    color: var(--button-secondary-text-press, #082f75);
    border-color: var(--button-primary-stroke-inverse-pressed, #0645b1);
    outline: 1px solid var(--button-primary-stroke-inverse-pressed, #0645b1);
  }
`;

const ButtonProfilePic = styled.img`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  margin-right: 10px;
`;

const Caret = styled.div`
  margin-left: 6px;
  display: flex;
`;

const MenuContainer = styled.div`
  position: absolute;
  right: 0;
  top: 56px;
  z-index: 100;
  width: 240px;
  box-shadow: 0px 20px 40px 0px #00000033;
  border-radius: 4px;
  background: white;
  padding: 8px;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  color: #111111;
  text-align: initial;

  > * {
    margin: 8px;
  }
`;

const UserName = styled.div`
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

type AccountMenuButtonProps = AccountMenuProps & {
  totalCount: number;
  open: boolean;
  setOpen: (openness: boolean) => void;
};

const AccountMenuButton = ({
  totalCount,
  open,
  setOpen,
  ...accountMenuProps
}: AccountMenuButtonProps) => {
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  useOnClickOutside([buttonRef, menuRef], () => setOpen(false));

  const handleClick = () => {
    // Only track opening the menu, not closing it.
    if (!open) {
      trackClick({ source: 'nav-bar--account-menu-button' });
    }
    setOpen(!open);
  };

  return (
    <>
      <UserButton
        ref={buttonRef}
        onClick={handleClick}
        aria-pressed={open}
        redesign={accountMenuProps.navVariant !== 'control'}
      >
        {isClient && currentUser != null && (
          <ButtonProfilePic src={$image_url(currentUser.photo)} />
        )}
        <PositionedDoubleDigitCountBubble count={totalCount} />
        <UserName>{isClient && currentUser?.first_name}</UserName>
        <Caret>
          <Icon name="keyboard_arrow_down" />
        </Caret>
      </UserButton>
      {open && (
        <MenuContainer ref={menuRef}>
          <AccountMenu {...accountMenuProps} />
        </MenuContainer>
      )}
    </>
  );
};

export default AccountMenuButton;
